import { useAtomValue } from 'jotai';
import { type PropsWithChildren, type ReactElement } from 'react';
import styled from 'styled-components';

import { Skeleton, SkeletonItem } from '../../atoms';

import { BackgroundViewAtom } from './BackgroundView.atom';

export type ActionProps = PropsWithChildren<{ disableSkeletons?: boolean; testID?: string }>;
export type TitleProps = PropsWithChildren<{ disableSkeletons?: boolean; testID?: string }>;
export type TopContentProps = PropsWithChildren<{ testID?: string }>;

export const LeftActions = ({ children, disableSkeletons, testID, ...props }: ActionProps) => {
	const { isLoading } = useAtomValue(BackgroundViewAtom);

	if (isLoading && !disableSkeletons) {
		return (
			<Skeleton testID={testID}>
				<S.SkeletonWrapper>
					<SkeletonItem height={25} width={45} />
				</S.SkeletonWrapper>
			</Skeleton>
		);
	}

	return (
		<S.LeftIconWrapper data-testid={testID} {...props}>
			{children}
		</S.LeftIconWrapper>
	);
};

export const RightActions = ({ children, disableSkeletons, testID, ...props }: ActionProps) => {
	const { isLoading } = useAtomValue(BackgroundViewAtom);

	if (isLoading && !disableSkeletons) {
		return (
			<Skeleton testID={testID}>
				<S.SkeletonWrapperRight>
					<SkeletonItem height={25} width={45} />
				</S.SkeletonWrapperRight>
			</Skeleton>
		);
	}

	return (
		<S.RightIconWrapper data-testid={testID} {...props}>
			{children}
		</S.RightIconWrapper>
	);
};

export const Title = ({ children, disableSkeletons, testID, ...props }: TitleProps) => {
	const { isLoading } = useAtomValue(BackgroundViewAtom);

	if (isLoading && !disableSkeletons) {
		return (
			<Skeleton testID={testID}>
				<S.SkeletonWrapper>
					<SkeletonItem height={25} width={120} />
				</S.SkeletonWrapper>
			</Skeleton>
		);
	}

	return (
		<p data-testid={testID} {...props}>
			{children}
		</p>
	);
};

export const Top = ({
	children,
	left,
	right,
	testID,
	...props
}: TopContentProps & { left?: ReactElement; right?: ReactElement }) => {
	const { title } = useAtomValue(BackgroundViewAtom);

	return (
		<S.TopWrapper data-testid={testID} {...props}>
			{left ?? <S.Spacer />}
			{children ? children : title ? <S.DefaultTitle>{title}</S.DefaultTitle> : null}
			{right ?? <S.Spacer />}
		</S.TopWrapper>
	);
};

namespace S {
	export const TopWrapper = styled.div`
		margin: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small} 0 ${theme.spacings.small}`};
		display: flex;
		justify-content: space-between;
		align-items: center;
	`;

	export const LeftIconWrapper = styled.div`
		display: flex;
		align-items: center;
		gap: 10px;
		flex: 1;
	`;

	export const RightIconWrapper = styled.div`
		display: flex;
		justify-content: flex-end;
		align-items: center;
		gap: 10px;
		flex: 1;
	`;

	export const DefaultTitle = styled.p`
		text-align: center;
		flex: 1;
		${({ theme }) => theme.fonts.text.text1};
		color: ${({ theme }) => theme.colors.text.onDark.enabled};
	`;

	export const Spacer = styled.div`
		flex: 1;
	`;

	export const SkeletonWrapper = styled.div`
		flex: 1;
	`;

	export const SkeletonWrapperRight = styled.div`
		flex: 1;
		display: flex;
		justify-content: flex-end;
	`;
}
